/*
* Dashboard for authenticated users
*/

import '@progress/kendo-ui';
import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import VirtualizedSelect from 'react-virtualized-select';
import { Grid } from '@progress/kendo-grid-react-wrapper';
import { Row, Button, ButtonToolbar } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { connectRequest } from 'redux-query';
import { withRouter } from 'react-router-dom';
import * as JSZip from 'jszip';
import fileDownload from 'js-file-download';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as ReduxQueries from '../queries';
import * as Actions from '../actions';
import ApiEndpoints from '../config/apiendpoints';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { FormUtilities } from '../common/formutilities';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { OrderStatuses } from '../order/orderpricing';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import { userHasRole } from 'src/utils/userUtils';

import './dashboard.css';

(window as any).JSZip = JSZip;

export interface IDashboardSearchProps extends AppComp.IAppComponentProps {
    filterByRegions: any;
    filterByStatuses: any;
    filterByUsers: any;
    filterByUsersManagers: any;
    filterByUsersCoordinators: any;
    navigateTo?: any;
    activeJobsFilterManager?: any;
    activeJobsFilterCoordinator?: any;
    activeJobsFilterStatusId?: any;
    activeJobsFilterRegionId?: any;
    downloadKML?: any;
}

export interface IDashboardSearchState extends AppComp.IAppComponentState {
    formChanges?: any;
}

class Dashboard extends AppComp.AppComponent<IDashboardSearchProps, IDashboardSearchState> {
    public MomentNow: any = moment();
    public isGridExporting: boolean = false;

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            GridUtilities.kendoGridColumnDefinition('associationId', this.appTextGridColumnHeader('association-id'), 50, 1, undefined, { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('associationName', this.appTextGridColumnHeader('association-name'), 210, 2,
                                                    GridUtilities.kendoGridLinkColumnTemplate('associationName', 'associationDetailLinkHref', this.appText('association-detail-link-title')),
                                                    {
                                                        aggregates: ['count'],
                                                        footerTemplate: `#=count# ${this.appTextResults()}`,
                                                        editable: this.onGridColumnEditable
                                                    }),

            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapAddress1', 1003),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapCity', 1004),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapState', 1005),
            GridUtilities.kendoGridColumnDefinitionHidden('associationLocationMapZip', 1006),

            GridUtilities.kendoGridColumnDefinition('associationLocation', this.appTextGridColumnHeader('location'), 110, 3, undefined, { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('serviceLevelName', this.appTextGridColumnHeader('service-level'), 100, 4, undefined, { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('acceptedPrice', this.appTextGridColumnHeader('price'), 80, 5, undefined,
                                                    {
                                                        aggregates: ['sum'],
                                                        footerTemplate: '#= kendo.toString(sum, "C") #',
                                                        format: '{0:c}',
                                                        editable: this.onGridColumnEditable
                                                    }),
            GridUtilities.kendoGridColumnDefinition('statusName', this.appTextGridColumnHeader('status'), 130, 6, '', { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('coordinatorUser', this.appTextGridColumnHeader('coordinator'), 80, 8, undefined, { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('managerUser', this.appTextGridColumnHeader('manager'), 60, 7, undefined, { editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('startDateValue', this.appTextGridColumnHeader('start-date'), 70, 9, undefined, { format: '{0:d}', editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('dueDateValue', this.appTextGridColumnHeader('due-date'), 70, 10, undefined, { format: '{0:d}', editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('numDays', this.appTextGridColumnHeader('num-days'), 50, 11, undefined, { editable: this.onGridColumnEditable }, 'numDaysSort'),
            GridUtilities.kendoGridColumnDefinition('viewOrderLink', '', 80, 12,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewOrderLink', 'viewOrderLinkHref', this.appText('view-order-link-title')),
                                                    { headerTemplate: '', editable: this.onGridColumnEditable }),
            GridUtilities.kendoGridColumnDefinition('viewInventoryLink', '', 90, 13,
                                                    GridUtilities.kendoGridLinkColumnTemplate('viewInventoryLink', 'viewInventoryLinkHref', this.appText('view-inventory-link-title')),
                                                    { headerTemplate: '', editable: this.onGridColumnEditable }),
        ],
        this.appText('no-results'),
        {
            dataBound: this.onGridDataBound,
            excelExport: this.onGridExcelExport,
            saveChanges: (e) => { this.handleExportKML(e); },
            editable: true,
            toolbar: [
                'excel',
                {
                    name: 'save',
                    text: 'Export to Google Earth',
                    iconClass: 'k-icon k-i-globe'
                },
                'pdf',
            ]
        },
        this.appTextPageTitle()
    );

    public constructor(props: IDashboardSearchProps) {
        super(props, 'app.dashboard.active-jobs.');

        this.state = {
            formChanges: {},
        };
    }

    public componentDidMount() {
        const enforce = this.enforceAdminOrEmployeeOnly();

        if (enforce) { this.props.navigateTo(NavigationLinks.clientDashboardHome()); }
    }

    public onGridColumnEditable(e: any) {
        return false;
    }

    public onGridExcelExport(e: any) {

        // console.log(`onGridExcelExport this.isGridExporting ${this.isGridExporting}`, { e, senderSaveAs: e.sender.saveAsExcel });

        if (!this.isGridExporting) {
            e.sender.showColumn(2);
            e.sender.showColumn(3);
            e.sender.showColumn(4);
            e.sender.showColumn(5);

            e.preventDefault();

            this.isGridExporting = true;

            // const now = new Date().getTime();
            // const fileName = `Active_Jobs_export_${now}.xlxs`;

            e.sender.saveAsExcel();
        } else {
            e.sender.hideColumn(2);
            e.sender.hideColumn(3);
            e.sender.hideColumn(4);
            e.sender.hideColumn(5);

            this.isGridExporting = false;
        }
    }

    public onGridDataBound(e: any) {
        // Apply conditional styling to grid rows/cells when data bound

        const data = e.sender.dataSource.view();

        const statusColumnIndex = _.findIndex(e.sender.columns, ['field', 'statusName']);
        const coordinatorColumnIndex = _.findIndex(e.sender.columns, ['field', 'coordinatorUser']);

        _.each(data, (v: any) => {
            const uid = v.get('uid');
            if (e.sender.tbody) {
                const row = e.sender.tbody.find('[data-uid="' + uid + '"]');

                if (v.get('statusNameCss')) {
                    row.children().eq(statusColumnIndex).addClass(v.get('statusNameCss'));
                    row.children().eq(coordinatorColumnIndex).addClass(v.get('statusNameCss'));
                }

                if (v.get('rowCss')) {
                    row.addClass(v.get('rowCss'));
                }
            }
        });

    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return records.map( r => {
            let record = _.cloneDeep(r);

            // Association name
            record.associationName = _.get(record, 'association.name', '');

            // Association name link details
            record.associationDetailLinkHref = (record.associationId) ? NavigationLinks.associationDetail(record.associationId) : '';

            // Association Location
            record.associationLocation = (record.association && record.association.location) ? Formatters.composeLocationCityState(record.association.location) : '';

            // Association Location Details for Map Export
            record.associationLocationMapAddress1 = Formatters.getStr(record, 'association.location.address1');
            record.associationLocationMapCity = Formatters.getStr(record, 'association.location.city');
            record.associationLocationMapState = Formatters.getStr(record, 'association.location.stateCode');
            record.associationLocationMapZip = Formatters.getStr(record, 'association.location.postalCode');

            // Service level name
            record.serviceLevelName = _.get(record, 'serviceLevel.name', '');

            // Status name
            let abbreviatedStatusName = '';
            if (_.get(record, 'status.statusId') === OrderStatuses.SITE_INSPECTION_PENDING) {
                abbreviatedStatusName = _.get(record, 'status.name').replace('Site Inspection Pending', `S/V Pending`);
            }
            if (_.get(record, 'status.statusId') === OrderStatuses.WAITING_ON_WORKSHEET) {
                abbreviatedStatusName = `W/O Worksheet`;
            }

            record.statusName = (abbreviatedStatusName) ? abbreviatedStatusName : _.get(record, 'status.name', '');

            // Manager
            record.managerUser = _.get(record, 'user.username', '');

            // Coordinator
            record.coordinatorUser = _.get(record, 'siteInspector.username', '');

            // Start date
            record.startDateValue = record.startDate ? new Date(record.startDate) : '';

            // Due date
            record.dueDateValue = record.dueDate ? new Date(record.dueDate) : '';

            // Calculate number of days & weeks until due
            record.numDays = record.dueDateValue ? moment(record.dueDateValue).diff(this.MomentNow, 'days') : '';
            record.numDaysSort = AppUtilities.isNumeric(record.numDays) ? Number(parseInt(record.numDays, 10)) : -99999;
            record.numWeeks = record.dueDateValue ? moment(record.dueDateValue).diff(this.MomentNow, 'weeks') : -1;

            const newNumDays: any = record.dueDateValue ? moment(record.dueDateValue).endOf('day').diff(moment(this.MomentNow).startOf('day'), 'days') : '';
            const newNumDaysSort: number = AppUtilities.isNumeric(newNumDays) ? Number(parseInt(newNumDays, 10)) : -99999;
            const newNumWeeks: number = record.dueDateValue ? moment(record.dueDateValue).endOf('day').diff(moment(this.MomentNow).startOf('day'), 'weeks') : -1;

            // console.log(`dashboard associd ${record.associationId} orderid ${record.orderId} dueDateValue ${record.dueDateValue} numDays ${record.numDays} numDaysSort ${record.numDaysSort}`, {
            //     dueDate: record.dueDate,
            //     dueDateValue: record.dueDateValue,
            //     numDays: record.numDays,
            //     numDaysSort: record.numDaysSort,
            //     numWeeks: record.numWeeks,
            //     rec: record,
            //     newNumDays,
            //     newNumDaysSort,
            //     newNumWeeks,
            // });

            record.numDays = newNumDays;
            record.numDaysSort = newNumDaysSort;
            record.numWeeks = newNumWeeks;

            // View order link
            record.viewOrderLink = '';
            record.viewOrderLinkHref = '';
            if (record.orderId) {
                record.viewOrderLink = this.appText('view-order');
                // record.viewOrderLinkHref = NavigationLinks.orderEdit(record.orderId);
                record.viewOrderLinkHref = NavigationLinks.orderDetail(record.orderId);
            }

            // View inventory link
            record.viewInventoryLink = '';
            record.viewInventoryLinkHref = '';
            if (record.association && record.associationId) {
                record.viewInventoryLink = this.appText('view-inventory');
                record.viewInventoryLinkHref = NavigationLinks.associationInventory(record.associationId);
            }

            /* CONDITIONAL ROW & CELL STYLING */
            const styling = this.getConditionalStyling(record);
            record.statusNameCss = styling.statusNameCss;
            record.rowCss = styling.rowCss;

            return record;
        });
    }

    public getConditionalStyling(record: any) {
        // Determine grid row conditional styling
        let result = {
            statusNameCss: '',
            rowCss: '',
        };

        if (!record) { return result; }

        // if DueDate not set, but startdate is set and within 21 days of now: SET row bg 255,192
        if (!record.dueDate && _.get(record, 'numDays') && _.get(record, 'numDaysSort') < 21) {
            result.rowCss = 'orangeBackground';
        }

        // If status is 'site inspection pending' and numdays < 25: SET status & coordinator cells bg color 255,0,0
        if (_.get(record, 'status.statusId') === OrderStatuses.SITE_INSPECTION_PENDING && _.get(record, 'numDays') && _.get(record, 'numDaysSort') < 25) {
            result.statusNameCss = 'redBackground';
        }

        // If Duedate is < -7: SET row bg color 192,0,0
        if (record.dueDateValue && _.get(record, 'numDaysSort') < 7) {
            result.rowCss = 'red192Background';
        }

        // Number of weeks before dueDate
        switch (record.numWeeks) {
            case 0:
                // If DueDate is in the same week number as today: SET row bg color 255,255,0
                result.rowCss = 'yellowBackground';
                break;

            case 1:
                // If DueDate is next week (use weeknum) from today: SET row bg color 255,192,0 and row text 156,101,0
                result.rowCss = 'orangeBackground';
                break;

            case 2:
                // If DueDate is in two weeks (use weeknum) from today: SET row bg color 0,176,80 and row text 156,0,6
                result.rowCss = 'greenBackground';
                break;

            default:
                break;
        }

        if (record.status.statusId === OrderStatuses.WAITING_ON_WORKSHEET) {
            // If status is 'waiting on worksheet': SET status & coordinator cells bg color purple
            result.rowCss = 'purpleBackground';
        }

        return result;
    }

    public handleSelectManager(e: any) {
        const value = e ? e.value : '';
        // this.props.dispatch(Actions.activeJobsFilterManager(value));
        this.props.activeJobsFilterManager(value);

    }

    public handleSelectCoordinator(e: any) {
        const value = e ? e.value : '';
        // this.props.dispatch(Actions.activeJobsFilterCoordinator(value));
        this.props.activeJobsFilterCoordinator(value);
    }

    public handleSelectStatusId(e: any) {
        const value = e ? e.value : 0;
        // this.props.dispatch(Actions.activeJobsFilterStatusId(value));
        this.props.activeJobsFilterStatusId(value);
    }

    public handleSelectRegionId(e: any) {
        const value = e ? e.value : 0;
        // this.props.dispatch(Actions.activeJobsFilterRegionId(value));
        this.props.activeJobsFilterRegionId(value);
    }

    public handleExportKML(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            {
                orders: _.map(_.get(this.props, 'search.results', []), (o) => {
                    const placemarkAddress = `${_.get(o, 'association.location.address1')} ${_.get(o, 'association.location.city')}, ${_.get(o, 'association.location.stateCode')} ${_.get(o, 'association.location.postalCode')}`;
                    const placemarkDescription = `Order #${_.get(o, 'orderId')}`;

                    return {
                        placemarkAddress: placemarkAddress,
                        placemarkDescription: placemarkDescription,
                        name: Formatters.getStr(o, 'association.name'),
                        address: Formatters.getStr(o, 'association.location.address1'),
                        city: Formatters.getStr(o, 'association.location.city'),
                        stateCode: Formatters.getStr(o, 'association.location.stateCode'),
                        postalCode: Formatters.getStr(o, 'association.location.postalCode'),
                        associationId: Formatters.getInt(o, 'associationId'),
                        orderId: Formatters.getInt(o, 'orderId'),
                    };
                })
            },
            {
                kmlConfig: {
                    folderName: 'Active Jobs',
                }
            }
        );

        this.props.downloadKML(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {

                const kmlFilename = `active-jobs-${moment().format('l LT')}.kml`;
                const kmlData = Formatters.getStr(response, 'text');

                fileDownload(kmlData, kmlFilename);
            } else {
              // Error during creation...
            }
        });
    }

    public render() {
        const { search, filterByUsers, filterByRegions, filterByStatuses, filterByUsersManagers, filterByUsersCoordinators, ...rest } = this.props;
        
        const rows = this.extendGridData(search.results);
        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows, {
            aggregate: [
                { field: 'associationName', aggregate: 'count' },
                { field: 'acceptedPrice', aggregate: 'sum' }
            ],
            sort: {
                dir: 'asc',
                field: 'numDaysSort',
            }
        });

        // Prepare select dropdown options
        let extendedUsers = [];
        if (AppUtilities.isArrayWithLength(filterByUsers)) {
            extendedUsers = filterByUsers.map(c => {
                c.sort = _.get(c, 'contact.firstName', '').trim().toLowerCase();
                if (!c.label) { c.label = Formatters.composeUsernameLabel(c, true); }

                return c;
            });
        }

        const filterOptionAny = this.appTextFilterOptionAny();
        const userManagers = FormUtilities.getExtendedFilterOptions(_.filter(extendedUsers, ((u) => userHasRole(u, 'administrator'))), 'username', 'sort', filterOptionAny, '', '_');
        const userCoordinators = FormUtilities.getExtendedFilterOptions(_.filter(extendedUsers, ((u) => userHasRole(u, 'employee'))), 'username', 'sort', filterOptionAny, '', '_');
        const regions = FormUtilities.getExtendedFilterOptions(filterByRegions, 'regionId', 'label', filterOptionAny, -1);
        const statuses = FormUtilities.getExtendedFilterOptions(filterByStatuses, 'statusId', 'statusId', this.appText('filter-option-status-active'), -1);

        // console.log('render', {
        //     users,
        //     usersCoordinators,
        //     usersManagers,
        //     extendedUsers,
        //     filterByUsersManagers,
        //     filterByUsersCoordinators,
        //     filterByUsers,
        //     extendedUsersCoordinators,
        //     extendedUsersManagers
        // });

        // console.log('gridoptions', { gridOptions: this.gridOptions, dataSource });

        const pageTitle = this.appTextResultsAndPageTitle(rows.length, this.commonMessagePaths.searchResults, false, 'list-of-active-jobs', true);

        const page = (
            <WhiteBoxPageSection title={pageTitle}>
                <Row>
                    <Col xs={12}>
                        <form onSubmit={e => { e.preventDefault(); }}>
                            <Row className="form-group">
                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-manager')}</label>
                                    { /*<VirtualizedSelect
                                        clearable={false}
                                        filterOptions={users.filter}
                                        name="manager-filter"
                                        onChange={e => this.handleSelectManager(e)}
                                        options={users.options}
                                        value={search.filters.manager}
                                    />*/}
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={userManagers.filter}
                                        name="manager-filter"
                                        onChange={e => this.handleSelectManager(e)}
                                        options={userManagers.options}
                                        value={search.filters.manager}
                                    />
                                </Col>
                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-coordinator')}</label>
                                        { /* <VirtualizedSelect
                                            clearable={false}
                                            filterOptions={users.filter}
                                            name="coordinator-filter"
                                            onChange={e => this.handleSelectCoordinator(e)}
                                            options={users.options}
                                            value={search.filters.coordinator}
                                        /> */ }
                                        <VirtualizedSelect
                                            clearable={false}
                                            filterOptions={userCoordinators.filter}
                                            name="coordinator-filter"
                                            onChange={e => this.handleSelectCoordinator(e)}
                                            options={userCoordinators.options}
                                            value={search.filters.coordinator}
                                        />
                                </Col>
                            </Row>

                            <Row className="form-group">
                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-status-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={statuses.filter}
                                        name="status-filter"
                                        onChange={e => this.handleSelectStatusId(e)}
                                        options={statuses.options}
                                        value={search.filters.statusId}
                                    />
                                </Col>

                                <Col xs={2} md={4} xl={2}>
                                    <label className="control-label">{this.appText('filter-by-region-id')}</label>
                                    <VirtualizedSelect
                                        clearable={false}
                                        filterOptions={regions.filter}
                                        name="region-filter"
                                        onChange={e => this.handleSelectRegionId(e)}
                                        options={regions.options}
                                        value={search.filters.regionId}
                                    />
                                </Col>
                            </Row>
                        </form>
                    </Col>
                </Row>

                <Row>
                    <Col xs={12}>
                        <div className="home-dashboard-table-wrapper">
                            <Grid dataSource={dataSource} {...this.gridOptions} />
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle /* 20200220 "" */} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getActiveJobsSearchUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchActiveJobsRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),
    _.get(props, 'search.filters.coordinator', ''),
    _.get(props, 'search.filters.manager', ''),
    _.get(props, 'search.filters.regionId', -1),
    _.get(props, 'search.filters.statusId', -1),
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByRegions: state.entities.searchActiveJobsRegions,
        filterByStatuses: state.entities.searchActiveJobsStatuses,
        filterByUsers: state.entities.searchActiveJobsUsers,
        filterByUsersManagers: state.entities.searchActiveJobsUsersManagers,
        filterByUsersCoordinators: state.entities.searchActiveJobsUsersCoordinators,
        search: {
            results: state.entities.searchActiveJobsResults,
            filters: {
                coordinator: state.searchFiltersActiveJobs.coordinator,
                manager: state.searchFiltersActiveJobs.manager,
                regionId: state.searchFiltersActiveJobs.regionId,
                statusId: state.searchFiltersActiveJobs.statusId,
            }
        },
    });
};

const mapDispatchToProps = dispatch => ({
    dispatch: (d) => dispatch(d),
    navigateTo: (path) => dispatch(push(path)),
    activeJobsFilterManager: (v) => dispatch(Actions.activeJobsFilterManager(v)),
    activeJobsFilterCoordinator: (v) => dispatch(Actions.activeJobsFilterCoordinator(v)),
    activeJobsFilterStatusId: (v) => dispatch(Actions.activeJobsFilterStatusId(v)),
    activeJobsFilterRegionId: (v) => dispatch(Actions.activeJobsFilterRegionId(v)),
    downloadKML: (d: any, a: string, o: boolean) => dispatch(MutateActions.downloadKMLMutateAsync(d, a, o)),
});

const DashboardContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
  )(Dashboard);

export default withRouter(DashboardContainer);



// WEBPACK FOOTER //
// ./src/dashboard/index.tsx